import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { CreateUserOrganizationRequest } from '../request-models/create-user-organization-request.model';
import { CreateUserOrganizationResponse } from '../response-models/create-user-organization-response.model';
import { isRecord } from '../utils/is-record';
import { idString, nullableString, omitUndefined } from '../utils/wire-value-utils';

/**
 * POST /admin/users-organizations (hub `admin-users-organizations-controller.ts`): creates (or reuses) a user
 * and an organization, plus the membership, in one transaction. A create that names an email with an existing
 * profile fails with `USER_ALREADY_EXISTS` (read it with `existingUserFromError`). The lookup that controller
 * also serves is `LookupUserService` (`admin/lookup-user`).
 */
@Injectable({ providedIn: 'root' })
export class UsersOrganizationsService extends ApiService {
  constructor() {
    super('admin/users-organizations');
  }

  /** POST /admin/users-organizations: creates (or reuses) the user and organization plus the membership. */
  create(body: CreateUserOrganizationRequest): Observable<CreateUserOrganizationResponse> {
    return this.request('POST', {}, toCreateResponse, toCreateUserOrganizationRequest(body));
  }
}

/** Only the hub's request fields that are set. */
export function toCreateUserOrganizationRequest(body: CreateUserOrganizationRequest): CreateUserOrganizationRequest {
  return omitUndefined<CreateUserOrganizationRequest>({
    role: body.role,
    email: body.email,
    firstName: body.firstName,
    lastName: body.lastName,
    existingProfileId: body.existingProfileId,
    organizationId: body.organizationId,
    organizationName: body.organizationName,
    organizationType: body.organizationType,
    ein: body.ein,
    ssn: body.ssn,
    streetAddress: body.streetAddress,
    streetAddressLine2: body.streetAddressLine2,
    city: body.city,
    state: body.state,
    zipCode: body.zipCode,
    country: body.country,
    organizationEmail: body.organizationEmail,
    organizationPhone: body.organizationPhone,
  });
}

function toCreateResponse(data: unknown): CreateUserOrganizationResponse {
  const source = isRecord(data) ? data : {};
  const profile = isRecord(source['profile']) ? source['profile'] : {};
  const organization = isRecord(source['organization']) ? source['organization'] : {};
  return {
    profileId: idString(profile['id']),
    profileEmail: nullableString(profile['email']) ?? null,
    organizationId: idString(organization['id']),
    organizationName: nullableString(organization['name']) ?? null,
  };
}
